'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent, ReactElement, SyntheticEvent, TouchEvent } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Direction } from 'radix-ui';
import {
  IconChevronLeft,
  IconChevronRight,
  IconMessageCircle,
  IconPhone,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { TelegramProductExtendedDto } from '@/generated';
import { ProductPrice } from '@/app/[lang]/(main)/components/shared/Product/productPrice';
import { ToggleFavourite } from '@/app/[lang]/(main)/components/shared/Product/ToggleFavorite';

const ZOOM_SCALE = 2.5;
const MAX_ZOOM = 4;
const ZOOM_HINT_MS = 2600;
const THUMBS_FADE_PX = 28;
const DOUBLE_TAP_MS = 300;
const TAP_SLOP_PX = 10;
const CLOSE_SWIPE_PX = 120;

type Zoom = { scale: number; x: number; y: number };

const NO_ZOOM: Zoom = { scale: 1, x: 0, y: 0 };

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const iconButtonClass =
  'size-9 rounded-full bg-surface-light text-text-primary hover:bg-primary-stroke-day hover:text-text-primary [&_svg:not([class*=size-])]:size-[18px]';

const navButtonClass =
  'absolute top-1/2 z-10 size-8 -translate-y-1/2 rounded-full bg-white/90 text-text-primary shadow-[0_2px_12px_rgba(0,0,0,0.12)] backdrop-blur-sm transition-opacity hover:bg-white hover:text-text-primary disabled:opacity-0 md:size-9 lg:size-10';

type Props = {
  onClose: () => void;
  photos: string[];
  activeIndexInit?: number;
  showButtons?: boolean;
  onClickCall: () => void;
  onClickWrite: () => void;
  onIndexChange?: (index: number) => void;
  product?: TelegramProductExtendedDto;
  canFavorite?: boolean;
};

function Hint({ label, children }: { label: string; children: ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side='bottom' sideOffset={6}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export const PhotosViewModal: React.FC<Props> = ({
  onClose,
  photos,
  activeIndexInit = 0,
  showButtons = true,
  onClickCall,
  onClickWrite,
  onIndexChange,
  product,
  canFavorite = false,
}) => {
  const t = useTranslations();
  const dir = Direction.useDirection();
  const total = photos.length;
  const hasMany = total > 1;

  const [api, setApi] = useState<CarouselApi>();
  const [thumbsApi, setThumbsApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(activeIndexInit);
  const [loaded, setLoaded] = useState<Set<number>>(() => new Set());
  const [zoom, setZoom] = useState<Zoom>(NO_ZOOM);
  const [isPanning, setIsPanning] = useState(false);
  const [closeDragY, setCloseDragY] = useState(0);
  const [isCloseDragging, setIsCloseDragging] = useState(false);
  const [showZoomHint, setShowZoomHint] = useState(true);
  const [thumbsEdges, setThumbsEdges] = useState({ start: false, end: false });

  const zoomRef = useRef(zoom);
  const contentRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const naturalSizes = useRef<Record<number, { w: number; h: number }>>({});
  const panStart = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const tapRef = useRef<{ time: number; x: number; y: number } | null>(null);
  const pointerDownRef = useRef<{ x: number; y: number; type: string } | null>(null);
  const touchRef = useRef<{ x: number; y: number; axis: 'x' | 'y' | null } | null>(null);
  const pinchRef = useRef<{ distance: number; scale: number; cx: number; cy: number } | null>(null);

  const isZoomed = zoom.scale > 1;

  useEffect(() => {
    const timer = setTimeout(() => setShowZoomHint(false), ZOOM_HINT_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!thumbsApi) return;
    const update = () => setThumbsEdges({ start: thumbsApi.canScrollPrev(), end: thumbsApi.canScrollNext() });
    update();
    thumbsApi.on('scroll', update).on('reInit', update).on('resize', update);
    return () => {
      thumbsApi.off('scroll', update).off('reInit', update).off('resize', update);
    };
  }, [thumbsApi]);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  const [mainOpts] = useState(() => ({
    startIndex: activeIndexInit,
    watchDrag: () => zoomRef.current.scale === 1 && !pinchRef.current,
  }));

  const [thumbsOpts] = useState(() => ({
    startIndex: activeIndexInit,
    dragFree: true,
    containScroll: 'keepSnaps' as const,
  }));

  useEffect(() => {
    if (!api) return;
    const onSelect = () => {
      setActiveIndex(api.selectedScrollSnap());
      setZoom(NO_ZOOM);
    };
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  useEffect(() => {
    thumbsApi?.scrollTo(activeIndex);
    onIndexChange?.(activeIndex);
  }, [thumbsApi, activeIndex, onIndexChange]);

  const markLoaded = useCallback((index: number, event: SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    naturalSizes.current[index] = { w: img.naturalWidth, h: img.naturalHeight };
    setLoaded((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  }, []);

  const clampZoom = useCallback(
    (next: Zoom): Zoom => {
      const frame = frameRef.current;
      if (!frame || next.scale <= 1) return NO_ZOOM;
      const { width: fw, height: fh } = frame.getBoundingClientRect();
      const natural = naturalSizes.current[activeIndex];
      const fit = natural ? Math.min(fw / natural.w, fh / natural.h) : 1;
      const w = natural ? natural.w * fit : fw;
      const h = natural ? natural.h * fit : fh;
      const maxX = Math.max(0, (w * next.scale - fw) / 2);
      const maxY = Math.max(0, (h * next.scale - fh) / 2);
      return { scale: next.scale, x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
    },
    [activeIndex],
  );

  const toggleZoom = useCallback(
    (clientX?: number, clientY?: number) => {
      if (zoomRef.current.scale > 1) {
        setZoom(NO_ZOOM);
        return;
      }
      const frame = frameRef.current;
      if (!frame) return;
      const rect = frame.getBoundingClientRect();
      const cx = clientX === undefined ? 0 : clientX - (rect.left + rect.width / 2);
      const cy = clientY === undefined ? 0 : clientY - (rect.top + rect.height / 2);
      setZoom(clampZoom({ scale: ZOOM_SCALE, x: cx * (1 - ZOOM_SCALE), y: cy * (1 - ZOOM_SCALE) }));
    },
    [clampZoom],
  );

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    pointerDownRef.current = { x: event.clientX, y: event.clientY, type: event.pointerType };
    if (!isZoomed) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    panStart.current = { px: event.clientX, py: event.clientY, x: zoom.x, y: zoom.y };
    setIsPanning(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const start = panStart.current;
    if (!start || pinchRef.current) return;
    setZoom(
      clampZoom({
        scale: zoomRef.current.scale,
        x: start.x + event.clientX - start.px,
        y: start.y + event.clientY - start.py,
      }),
    );
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    panStart.current = null;
    setIsPanning(false);

    const down = pointerDownRef.current;
    if (event.pointerType !== 'touch' || !down) return;
    const moved = Math.hypot(event.clientX - down.x, event.clientY - down.y);
    if (moved > TAP_SLOP_PX) {
      tapRef.current = null;
      return;
    }
    const now = Date.now();
    const prev = tapRef.current;
    if (prev && now - prev.time < DOUBLE_TAP_MS && Math.hypot(event.clientX - prev.x, event.clientY - prev.y) < 30) {
      tapRef.current = null;
      toggleZoom(event.clientX, event.clientY);
    } else {
      tapRef.current = { time: now, x: event.clientX, y: event.clientY };
    }
  };

  const handleDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (pointerDownRef.current?.type === 'touch') return;
    toggleZoom(event.clientX, event.clientY);
  };

  const getPinchPoint = (event: TouchEvent<HTMLDivElement>) => {
    const [a, b] = [event.touches[0], event.touches[1]];
    const rect = frameRef.current?.getBoundingClientRect();
    const midX = (a.clientX + b.clientX) / 2;
    const midY = (a.clientY + b.clientY) / 2;
    return {
      distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
      x: rect ? midX - (rect.left + rect.width / 2) : 0,
      y: rect ? midY - (rect.top + rect.height / 2) : 0,
    };
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    setShowZoomHint(false);
    if (event.touches.length === 2) {
      const point = getPinchPoint(event);
      const current = zoomRef.current;
      touchRef.current = null;
      setCloseDragY(0);
      setIsCloseDragging(false);
      pinchRef.current = {
        distance: point.distance,
        scale: current.scale,
        cx: (point.x - current.x) / current.scale,
        cy: (point.y - current.y) / current.scale,
      };
      setIsPanning(true);
      return;
    }
    if (isZoomed || event.touches.length > 1) {
      touchRef.current = null;
      return;
    }
    touchRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY, axis: null };
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const pinch = pinchRef.current;
    if (pinch && event.touches.length === 2) {
      const point = getPinchPoint(event);
      const scale = clamp((pinch.scale * point.distance) / pinch.distance, 1, MAX_ZOOM);
      setZoom(clampZoom({ scale, x: point.x - pinch.cx * scale, y: point.y - pinch.cy * scale }));
      return;
    }
    const start = touchRef.current;
    if (!start) return;
    const dx = event.touches[0].clientX - start.x;
    const dy = event.touches[0].clientY - start.y;
    if (!start.axis) {
      if (Math.hypot(dx, dy) < TAP_SLOP_PX) return;
      start.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x';
    }
    if (start.axis === 'y') {
      setIsCloseDragging(true);
      setCloseDragY(Math.max(0, dy));
    }
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (pinchRef.current) {
      if (event.touches.length < 2) {
        pinchRef.current = null;
        setIsPanning(false);
        if (zoomRef.current.scale < 1.1) setZoom(NO_ZOOM);
      }
      return;
    }
    const wasClosing = touchRef.current?.axis === 'y';
    touchRef.current = null;
    if (!wasClosing) return;
    setIsCloseDragging(false);
    if (closeDragY > CLOSE_SWIPE_PX) {
      onClose();
      return;
    }
    setCloseDragY(0);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || !api) return;
    const isRtl = dir === 'rtl';
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      api.scrollTo(event.key === 'Home' ? 0 : total - 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      if (isRtl) api.scrollNext();
      else api.scrollPrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      if (isRtl) api.scrollPrev();
      else api.scrollNext();
    }
  };

  const zoomLabel = isZoomed ? t('photoViewer.zoomOut') : t('photoViewer.zoomIn');
  const fadeSide = dir === 'rtl' ? 'left' : 'right';
  const thumbsMask =
    thumbsEdges.start || thumbsEdges.end
      ? `linear-gradient(to ${fadeSide}, ${thumbsEdges.start ? 'transparent' : 'black'}, black ${THUMBS_FADE_PX}px, black calc(100% - ${THUMBS_FADE_PX}px), ${thumbsEdges.end ? 'transparent' : 'black'})`
      : undefined;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        ref={contentRef}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          contentRef.current?.focus();
        }}
        onKeyDown={handleKeyDown}
        overlayClassName='bg-black/60 backdrop-blur-md'
        style={
          closeDragY
            ? { transform: `translateY(${closeDragY}px)`, opacity: 1 - Math.min(closeDragY / 500, 0.5) }
            : undefined
        }
        className={cn(
          'inset-0 top-0 start-0 flex h-dvh max-h-none w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-bg-day p-0 text-text-primary shadow-none sm:max-w-none rtl:translate-x-0',
          'md:inset-auto md:top-1/2 md:start-1/2 md:h-[min(880px,calc(100dvh-48px))] md:w-[min(1160px,calc(100vw-48px))] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-[28px] md:shadow-2xl md:rtl:translate-x-1/2',
          !isCloseDragging && 'transition-[transform,opacity]',
        )}
      >
        <TooltipProvider delayDuration={400}>
          <DialogTitle className='sr-only'>{t('photoViewer.title', { current: activeIndex + 1, total })}</DialogTitle>

          <div className='flex h-14 shrink-0 items-center justify-between gap-3 px-4 md:h-16 md:px-6'>
            <span
              className='rounded-full bg-surface-light px-3 py-1 text-sm font-medium text-text-primary tabular-nums'
              aria-hidden
            >
              {activeIndex + 1}
              <span className='text-text-secondary'> / {total}</span>
            </span>

            <div className='flex items-center gap-2'>
              <Hint label={zoomLabel}>
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={() => toggleZoom()}
                  aria-label={zoomLabel}
                  aria-pressed={isZoomed}
                  className={cn(iconButtonClass, 'hidden md:inline-flex')}
                >
                  {isZoomed ? <IconZoomOut /> : <IconZoomIn />}
                </Button>
              </Hint>
              <Hint label={t('photoViewer.close')}>
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={onClose}
                  aria-label={t('photoViewer.close')}
                  className={iconButtonClass}
                >
                  <IconX />
                </Button>
              </Hint>
            </div>
          </div>

          <div
            className={cn(
              'group relative min-h-0 flex-1 overflow-hidden bg-surface-lightest md:mx-6 md:rounded-2xl',
              !hasMany && !showButtons && !product && 'md:mb-6',
            )}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
          >
            <Carousel setApi={setApi} opts={mainOpts} className='h-full [&_[data-slot=carousel-content]]:h-full'>
              <CarouselContent className='ms-0 h-full'>
                {photos.map((src, index) => {
                  const isActive = index === activeIndex;
                  const isLoaded = loaded.has(index);
                  return (
                    <CarouselItem key={`${src}-${index}`} className='relative h-full ps-0'>
                      <div
                        ref={isActive ? frameRef : undefined}
                        onPointerDown={isActive ? handlePointerDown : undefined}
                        onPointerMove={isActive ? handlePointerMove : undefined}
                        onPointerUp={isActive ? handlePointerUp : undefined}
                        onPointerCancel={isActive ? handlePointerUp : undefined}
                        onDoubleClick={isActive ? handleDoubleClick : undefined}
                        style={isActive ? { touchAction: isZoomed ? 'none' : 'pan-y' } : undefined}
                        className={cn(
                          'absolute inset-0 overflow-hidden',
                          isActive && isZoomed && (isPanning ? 'cursor-grabbing' : 'cursor-grab'),
                          isActive && !isZoomed && 'pointer-fine:cursor-zoom-in',
                        )}
                      >
                        {!isLoaded && <Skeleton className='absolute inset-0 rounded-none bg-surface-light' />}
                        <div
                          className={cn(
                            'absolute inset-0 will-change-transform',
                            !isPanning && 'transition-transform duration-200 ease-out',
                          )}
                          style={
                            isActive
                              ? { transform: `translate3d(${zoom.x}px, ${zoom.y}px, 0) scale(${zoom.scale})` }
                              : undefined
                          }
                        >
                          <Image
                            src={src}
                            alt={t('photoViewer.title', { current: index + 1, total })}
                            fill
                            sizes='(min-width: 768px) 1160px, 100vw'
                            quality={75}
                            draggable={false}
                            loading={Math.abs(index - activeIndex) <= 1 ? 'eager' : 'lazy'}
                            onLoad={(event) => markLoaded(index, event)}
                            className={cn(
                              'object-contain object-center select-none transition-opacity duration-300',
                              isLoaded ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                        </div>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
            </Carousel>

            <div
              aria-hidden
              className={cn(
                'pointer-events-none absolute bottom-3 start-1/2 z-10 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm transition-opacity duration-500 pointer-fine:hidden rtl:translate-x-1/2',
                showZoomHint && !isZoomed ? 'opacity-100' : 'opacity-0',
              )}
            >
              {t('photoViewer.zoomHint')}
            </div>

            {hasMany && (
              <>
                <Hint label={t('photoViewer.prev')}>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => api?.scrollPrev()}
                    disabled={activeIndex === 0}
                    aria-label={t('photoViewer.prev')}
                    className={cn(navButtonClass, 'start-2 md:start-3 lg:start-4')}
                  >
                    <IconChevronLeft className='size-5 rtl:rotate-180' />
                  </Button>
                </Hint>
                <Hint label={t('photoViewer.next')}>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => api?.scrollNext()}
                    disabled={activeIndex === total - 1}
                    aria-label={t('photoViewer.next')}
                    className={cn(navButtonClass, 'end-2 md:end-3 lg:end-4')}
                  >
                    <IconChevronRight className='size-5 rtl:rotate-180' />
                  </Button>
                </Hint>
              </>
            )}
          </div>

          {hasMany && (
            <div
              className={cn(
                'shrink-0 px-4 pt-3 md:px-6 md:pt-4',
                !showButtons && !product && 'pb-[max(16px,env(safe-area-inset-bottom))] md:pb-6',
              )}
            >
              <Carousel
                setApi={setThumbsApi}
                opts={thumbsOpts}
                className='mx-auto w-fit max-w-full'
                style={thumbsMask ? { maskImage: thumbsMask, WebkitMaskImage: thumbsMask } : undefined}
              >
                <CarouselContent className='-ms-2 p-1'>
                  {photos.map((src, index) => {
                    const isActive = index === activeIndex;
                    return (
                      <CarouselItem key={`${src}-${index}`} className='basis-auto ps-2'>
                        <button
                          type='button'
                          onClick={() => api?.scrollTo(index)}
                          aria-label={t('photoViewer.title', { current: index + 1, total })}
                          aria-current={isActive}
                          className={cn(
                            'relative block h-10 w-14 cursor-pointer overflow-hidden rounded-[4px] bg-surface-light transition outline-offset-2 focus-visible:outline-2 focus-visible:outline-main-green xxs:h-9 xxs:w-12 lg:h-12 lg:w-16',
                            isActive
                              ? 'opacity-100 outline-2 outline-main-green'
                              : 'opacity-55 hover:opacity-100 pointer-fine:hover:-translate-y-px',
                          )}
                        >
                          <Image src={src} alt='' fill sizes='64px' className='object-cover' draggable={false} />
                        </button>
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
              </Carousel>
            </div>
          )}

          {(showButtons || product) && (
            <div className='flex shrink-0 flex-col gap-3 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] md:flex-row md:items-center md:gap-6 md:px-6 md:pt-4 md:pb-6'>
              {product && (
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-base font-medium text-text-primary'>{product.name}</p>
                  <p className='truncate text-xl leading-6 font-semibold text-text-primary'>
                    <ProductPrice surface='detail' product={product} />
                  </p>
                </div>
              )}
              {(showButtons || (product && canFavorite)) && (
                <div className='flex w-full items-center gap-2 md:w-auto md:shrink-0'>
                  {showButtons && (
                    <>
                      <Button onClick={onClickCall} className='h-11 min-w-0 flex-1 px-5 md:flex-none'>
                        <IconPhone className='size-[18px]' />
                        <span className='truncate'>{t('product.callBtn')}</span>
                      </Button>
                      <Button
                        onClick={onClickWrite}
                        className='h-11 min-w-0 flex-1 bg-primary px-5 text-white hover:bg-primary/90 md:flex-none'
                      >
                        <IconMessageCircle className='size-[18px]' />
                        <span className='truncate'>{t('product.writeBtn')}</span>
                      </Button>
                    </>
                  )}
                  {product && canFavorite && (
                    <div className='size-11 shrink-0 rounded-[12px] bg-surface-light transition-colors hover:bg-primary-stroke-day'>
                      <ToggleFavourite
                        isFavourite={product.isFavourite}
                        productId={product.id}
                        productOwnerId={product.telegramUserId}
                        size={22}
                        className='size-full grow-0! items-center! justify-center gap-0!'
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
};
